import React, { useState, useEffect, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { ArrowLeftIcon, ViewColumnsIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import dressLikesService, { DressLike } from '../services/dressLikes';
import DressCard from '../components/DressCard';

const LikedDresses: React.FC = () => {
  const [likedDresses, setLikedDresses] = useState<DressLike[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isReordering, setIsReordering] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadLikedDresses = useCallback(async (pageNum: number, reset: boolean = false) => {
    try {
      if (pageNum === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);

      const response = await dressLikesService.getLikedDresses(pageNum, 20);
      
      if (reset) {
        setLikedDresses(response.data);
      } else {
        setLikedDresses(prev => [...prev, ...response.data]);
      }
      
      setHasMore(response.pagination.page < response.pagination.totalPages);
    } catch (err) {
      console.error('Failed to load liked dresses:', err);
      setError('Failed to load liked dresses. Please try again.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    loadLikedDresses(1, true);
  }, [loadLikedDresses]);

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      setPage(prev => prev + 1);
      loadLikedDresses(page + 1);
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const items = Array.from(likedDresses);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Optimistic update
    setLikedDresses(items);
    setIsReordering(true);

    try {
      const orderedLikeIds = items.map(item => item.id);
      await dressLikesService.reorderLikes(orderedLikeIds);
    } catch (error) {
      console.error('Failed to reorder:', error);
      // Revert on error
      loadLikedDresses(1, true);
    } finally {
      setIsReordering(false);
    }
  };

  const handleUnlike = (dressId: string) => {
    setLikedDresses(prev => prev.filter(like => like.dress.id !== dressId));
  };

  if (loading && page === 1) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-4xl mx-auto text-center py-12">
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={() => loadLikedDresses(1, true)} className="btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <Link to="/home" className="flex items-center text-gray-600 hover:text-gray-900">
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              <span>Back</span>
            </Link>
            
            <button
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
              className="p-2 text-gray-600 hover:text-gray-900"
              aria-label={`Switch to ${viewMode === 'grid' ? 'list' : 'grid'} view`}
            >
              {viewMode === 'grid' ? (
                <ViewColumnsIcon className="h-5 w-5" />
              ) : (
                <Squares2X2Icon className="h-5 w-5" />
              )}
            </button>
          </div>
          
          <h1 className="text-2xl font-serif text-gray-900">Liked Dresses</h1>
          {likedDresses.length > 0 && (
            <p className="text-sm text-gray-500 mt-1">
              {likedDresses.length} {likedDresses.length === 1 ? 'dress' : 'dresses'} liked
            </p>
          )}
        </div>
      </header>

      {/* Instructions */}
      {likedDresses.length > 0 && viewMode === 'list' && (
        <div className="px-4 py-2 bg-primary-50 text-center">
          <p className="text-sm text-gray-700">
            Drag and drop to reorder your favorites
          </p>
        </div>
      )}

      {/* Content */}
      <div className="px-4 py-4">
        {likedDresses.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">You haven't liked any dresses yet</p>
            <Link to="/home/catalog" className="btn-primary inline-block">
              Browse Catalog
            </Link>
          </div>
        ) : (
          <>
            {viewMode === 'grid' ? (
              // Grid View
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-4xl mx-auto">
                {likedDresses.map((like) => (
                  <DressCard
                    key={like.id}
                    dress={like.dress}
                    isLiked={true}
                    onLikeChange={() => handleUnlike(like.dress.id)}
                  />
                ))}
              </div>
            ) : (
              // List View with Drag and Drop
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="liked-dresses">
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="max-w-2xl mx-auto space-y-4"
                    >
                      {likedDresses.map((like, index) => (
                        <Draggable 
                          key={like.id} 
                          draggableId={like.id} 
                          index={index}
                          isDragDisabled={isReordering}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`bg-white rounded-lg shadow-sm overflow-hidden ${
                                snapshot.isDragging ? 'shadow-lg' : ''
                              }`}
                            >
                              <div className="flex items-center p-4">
                                <div className="flex-shrink-0 w-20 h-24 bg-gray-100 rounded">
                                  {like.dress.thumbnailUrl && (
                                    <img
                                      src={like.dress.thumbnailUrl}
                                      alt={like.dress.name}
                                      className="w-full h-full object-cover rounded"
                                    />
                                  )}
                                </div>
                                
                                <div className="ml-4 flex-1">
                                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                                    {like.dress.designer.name}
                                  </p>
                                  <h3 className="font-medium text-gray-900 mt-1">
                                    {like.dress.name}
                                  </h3>
                                  <p className="text-xs text-gray-400 mt-1">
                                    Liked on {new Date(like.likedAt).toLocaleDateString()}
                                  </p>
                                </div>
                                
                                <div className="ml-4 text-gray-400">
                                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                  </svg>
                                </div>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}

            {/* Load More Button */}
            {hasMore && (
              <div className="text-center mt-8">
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="btn-secondary"
                >
                  {loadingMore ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500 mx-auto"></div>
                  ) : (
                    'Load More'
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LikedDresses;